/**
 * Konfigurace pro Mapy.com REST API
 *
 * API dokumentace: https://developer.mapy.com/
 */

/**
 * API klíč pro Mapy.com
 */
const API_KEY = '7FCualwZ_hzBe2-Mxb3yBKYKIneQh5J4UNmsbEGcj1o';

/**
 * Vrací API klíč
 */
export const getApiKey = (): string => {
    return API_KEY;
};

// Pro zpětnou kompatibilitu
export const MAPY_API_KEY = API_KEY;

export const MAPY_API_CONFIG = {
    get apiKey() {
        return getApiKey();
    },

    /**
     * Map Tiles API
     * Dokumentace: https://developer.mapy.com/en/rest-api-mapy-cz/function/map-tiles/
     */
    tiles: {
        baseUrl: 'https://api.mapy.com/v1/maptiles',
        mapsets: {
            basic: 'basic', // Základní mapa
            outdoor: 'outdoor', // Turistická mapa (nahrazuje DEF_TURIST)
            winter: 'winter', // Zimní mapa
            aerial: 'aerial', // Letecké snímky
        },
        tileSize: 256,
        minZoom: 0,
        maxZoom: 19,
    },

    /**
     * JS Panorama API
     * Dokumentace: https://developer.mapy.com/js-panorama-2/
     */
    panorama: {
        scriptUrl: 'https://api.mapy.cz/js/panorama/v1/panorama.js',
        defaultRadius: 50, // Výchozí radius pro hledání panorama v metrech
        maxRadius: 5000, // Maximální radius pro hledání
        toleranceStep: 500, // Krok zvyšování tolerance při neúspěchu
    },

    /**
     * Suggest API (Autocomplete)
     * Dokumentace: https://developer.mapy.com/en/rest-api-mapy-cz/function/geocoding/
     */
    suggest: {
        baseUrl: 'https://api.mapy.com/v1/suggest',
        defaultLimit: 5,
        // Hranice České republiky pro omezení výsledků
        bounds: '48.5370786,12.0921668|51.0746358,18.8927040',
        lang: 'cs',
    },

    /**
     * Povinné atributy pro zobrazení mapy
     */
    attribution: {
        text: '&copy; Seznam.cz a.s. a další',
        url: 'https://api.mapy.com/copyright',
        logoUrl: 'https://api.mapy.com/img/api/logo.svg',
        logoSmallUrl: 'https://api.mapy.com/img/api/logo-small.svg',
    },
};

/**
 * Typ pro mapové vrstvy
 */
export type MapsetType = keyof typeof MAPY_API_CONFIG.tiles.mapsets;

/**
 * Generuje URL pro mapové dlaždice
 */
export const getTileUrl = (mapset: MapsetType = 'basic'): string => {
    const { baseUrl, tileSize } = MAPY_API_CONFIG.tiles;
    const mapsetName = MAPY_API_CONFIG.tiles.mapsets[mapset];
    return `${baseUrl}/${mapsetName}/${tileSize}/{z}/{x}/{y}?apikey=${getApiKey()}`;
};

/**
 * Generuje URL pro suggest API
 */
export const getSuggestUrl = (query: string, limit?: number): string => {
    const { baseUrl, defaultLimit, lang, bounds } = MAPY_API_CONFIG.suggest;
    const params = new URLSearchParams({
        apikey: getApiKey(),
        query,
        limit: String(limit || defaultLimit),
        lang,
    });

    // Bounds je ve formátu "lat,lon|lat,lon", ale API očekává jiný formát
    // Přidáme jako locality parametr pro omezení na ČR
    if (bounds) {
        params.append('locality', 'CZ');
    }

    return `${baseUrl}?${params.toString()}`;
};

/**
 * Typy pro Suggest API response
 */
export interface SuggestItem {
    name: string;
    label: string;
    position: {
        lon: number;
        lat: number;
    };
    type: string;
    location: string;
    regionalStructure: Array<{
        name: string;
        type: string;
        isoCode?: string;
    }>;
    zip?: string;
}

export interface SuggestResponse {
    items: SuggestItem[];
}

/**
 * Typy pro Panorama API
 */
export interface PanoramaInfo {
    lon: number;
    lat: number;
    date: string;
}

export interface PanoramaInstance {
    info?: PanoramaInfo;
    error: string;
    errorCode: 'NONE' | 'PANORAMA_NOT_FOUND' | 'MISSING_API_KEY' | 'WRONG_API_KEY' | 'LIMIT_EXCEEDED' | 'TIMEOUT';
    addListener: (event: string, callback: (data: any) => void) => void;
    removeListener: (event: string, callback: (data: any) => void) => void;
    getCamera: () => { yaw: number; pitch: number; fov: number };
    setCamera: (camera: { yaw?: number; pitch?: number; fov?: number }) => void;
    destroy: () => void;
}

export interface PanoramaExistsResult {
    exists: boolean;
    info?: PanoramaInfo;
    error?: string;
    errorCode?: 'NONE' | 'PANORAMA_NOT_FOUND' | 'MISSING_API_KEY' | 'WRONG_API_KEY' | 'LIMIT_EXCEEDED' | 'TIMEOUT';
}

/**
 * Ověří, zda je API klíč platný (vrací true pokud OK, false pokud 403/limit)
 */
export const checkApiKeyValid = async (): Promise<boolean> => {
    try {
        // Použijeme suggest endpoint pro ověření - je rychlý a jednoduchý
        const response = await fetch(`${MAPY_API_CONFIG.suggest.baseUrl}?apikey=${getApiKey()}&query=Praha&limit=1`);
        return response.ok;
    } catch {
        return false;
    }
};
